import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import authService from '../../services/auth';
import './UsernameSetup.scss';
import '../AccountCreated/AccountCreated.scss';

const UsernameSetup = () => {
  const { user, isAuthenticated, loading, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState('idle'); // 'idle', 'checking', 'available', 'unavailable'

  // Check if this is a dev route
  const isDevMode = location.pathname.startsWith('/dev/');

  const getValidationStatus = (value) => {
    return {
      hasValue: !!value,
      hasMinLength: value.length >= 3,
      hasMaxLength: value.length <= 20,
      hasValidChars: /^[a-zA-Z0-9]*$/.test(value) && (value !== '')
    };
  };

  const validation = getValidationStatus(username);
  const allValidationsPassed = validation.hasValue && validation.hasMinLength && validation.hasMaxLength && validation.hasValidChars;

  // Check username availability when all validations pass
  useEffect(() => {
    if (!allValidationsPassed || !username) {
      setAvailabilityStatus('idle');
      return;
    }

    const checkAvailability = async () => {
      setAvailabilityStatus('checking');
      
      try {
        const data = await authService.checkUsernameAvailability(username);
        setAvailabilityStatus(data.available ? 'available' : 'unavailable');
      } catch (error) {
        console.error('Error checking username availability:', error);
        setAvailabilityStatus('idle');
      }
    };

    const timeoutId = setTimeout(checkAvailability, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [username, allValidationsPassed]);

  // Skip redirects in dev mode
  if (!isDevMode) {
    // Redirect if not logged in
    if (!isAuthenticated && !loading) {
      return <Navigate to="/login" replace />;
    }

    // Redirect if already has username
    if (user && user.username) {
      return <Navigate to="/" replace />;
    }
  }

  if (loading) {
    return (
      <div className="username-setup">
        <div className="username-setup__container">
          <div className="username-setup__loading">Loading...</div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allValidationsPassed || availabilityStatus !== 'available') {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const data = await authService.setUsername(username);
      
      // Refresh user data and show welcome component inline
      await refreshUser();
      setShowWelcome(true);
    } catch (error) {
      console.error('Username setup error:', error);
      // Extract error message from the response
      const errorMessage = error.response?.error || error.message || 'Network error. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  // Show welcome component after successful username creation
  if (showWelcome) {
    return (
      <div className="account-created">
        <div className="account-created__container">
          <div className="account-created__content">
            <div className="account-created__icon">🎉</div>
            <h1 className="account-created__title">Welcome to GuessNexus!</h1>
            <p className="account-created__description">
              Your account has been created successfully.
            </p>
            
            <div className="account-created__features">
              <div className="account-created__feature">
                <span className="account-created__feature-icon">🎯</span>
                <span className="account-created__feature-text">Daily challenges</span>
              </div>
              <div className="account-created__feature">
                <span className="account-created__feature-icon">🏆</span>
                <span className="account-created__feature-text">Achievements & streaks</span>
              </div>
              <div className="account-created__feature">
                <span className="account-created__feature-icon">📊</span>
                <span className="account-created__feature-text">Track your progress</span>
              </div>
            </div>

            <button 
              className="account-created__button"
              onClick={() => navigate('/', { replace: true })}
            >
              Continue to Games
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="username-setup">
      <div className="username-setup__container">
        <div className="username-setup__header">
          <h1 className="username-setup__title">Choose Your Username</h1>
        </div>

        <form onSubmit={handleSubmit} className="username-setup__form">
          <div className="username-setup__form-group">
            <label htmlFor="username" className="username-setup__label">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={handleUsernameChange}
              placeholder="Enter your username"
              disabled={isSubmitting}
              maxLength={20}
              className="username-setup__input"
            />
          </div>

          <div className="username-setup__availability">
            {username && availabilityStatus === 'checking' && (
              <div className="username-setup__availability-checking">
                <div className="username-setup__spinner"></div>
                <span>Checking availability...</span>
              </div>
            )}
            {username && availabilityStatus === 'available' && (
              <div className="username-setup__availability-available">
                <span className="username-setup__checkmark">✓</span>
                <span>Username available</span>
              </div>
            )}
            {username && availabilityStatus === 'unavailable' && (
              <div className="username-setup__availability-unavailable">
                <span className="username-setup__x-mark">✗</span>
                <span>Username not available</span>
              </div>
            )}
          </div>

          <div className="username-setup__form-group">
          </div>

          <div className="username-setup__rules">
            <h4 className="username-setup__rules-title">Username Requirements:</h4>
            <ul className="username-setup__rules-list">
              <li className={`username-setup__rule ${validation.hasMinLength && validation.hasMaxLength ? 'username-setup__rule--valid' : 'username-setup__rule--invalid'}`}>
                <span className="username-setup__requirement-icon">{validation.hasMinLength && validation.hasMaxLength ? '✓' : '✗'}</span>
                3-20 characters long
              </li>
              <li className={`username-setup__rule ${validation.hasValidChars ? 'username-setup__rule--valid' : 'username-setup__rule--invalid'}`}>
                <span className="username-setup__requirement-icon">{validation.hasValidChars ? '✓' : '✗'}</span>
                Only letters (a-z, A-Z) and numbers (0-9)
              </li>
            </ul>
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting || !allValidationsPassed || availabilityStatus !== 'available'}
            className="username-setup__submit"
          >
            {isSubmitting ? 'Setting Username...' : 'Set Username'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UsernameSetup;